import { ARENA } from './constants';
import { SeededRandom } from '../utils/random';

export type EnvironmentType = 'forest' | 'desert' | 'glacier';

interface ForestLayer {
  trunks: Array<{ x: number; width: number; height: number; color: string }>;
  canopy: Array<{
    x: number;
    y: number;
    radius: number;
    color: string;
    swaySpeed: number;
    swayMagnitude: number;
    phase: number;
  }>;
  depth: number;
  stones: Array<{ x: number; width: number; height: number; color: string }>;
}

interface DesertScene {
  dunes: Array<{ baseY: number; amplitude: number; phase: number; color: string; speed: number }>;
  pyramids: Array<{ x: number; size: number; colors: [string, string, string] }>;
  dust: Array<{ x: number; y: number; radius: number; speed: number; drift: number }>;
}

interface GlacierScene {
  ridges: Array<{ x: number; width: number; height: number; colors: [string, string]; offset: number }>;
  shards: Array<{ x: number; y: number; height: number; width: number; sway: number; phase: number }>;
  aurora: Array<{ amplitude: number; frequency: number; phase: number; color: string }>;
}

interface GroundPalette {
  base: string;
  glow: string;
}

export class EnvironmentBackdrop {
  private readonly environment: EnvironmentType;
  private readonly forest?: ForestLayer[];
  private readonly desert?: DesertScene;
  private readonly glacier?: GlacierScene;
  private readonly groundPalette: GroundPalette;

  constructor(seed: number, environment: EnvironmentType) {
    this.environment = environment;
    const random = new SeededRandom(seed);
    switch (environment) {
      case 'forest':
        this.forest = this.generateForest(random);
        this.groundPalette = { base: '#132515', glow: 'rgba(104, 223, 170, 0.14)' };
        break;
      case 'desert':
        this.desert = this.generateDesert(random);
        this.groundPalette = { base: '#5e3b14', glow: 'rgba(255, 196, 102, 0.18)' };
        break;
      case 'glacier':
        this.glacier = this.generateGlacier(random);
        this.groundPalette = { base: '#102236', glow: 'rgba(132, 214, 255, 0.2)' };
        break;
      default:
        this.groundPalette = { base: '#132515', glow: 'rgba(104, 223, 170, 0.14)' };
        break;
    }
  }

  render(ctx: CanvasRenderingContext2D, time: number): void {
    switch (this.environment) {
      case 'forest':
        this.renderForest(ctx, time);
        break;
      case 'desert':
        this.renderDesert(ctx, time);
        break;
      case 'glacier':
        this.renderGlacier(ctx, time);
        break;
      default:
        break;
    }
  }

  palette(): GroundPalette {
    return this.groundPalette;
  }

  private generateForest(random: SeededRandom): ForestLayer[] {
    const trunkPalette = ['#2f1b13', '#382317', '#452b1c'];
    const leafPalette = ['#2e9a6c', '#38b077', '#4fc98d', '#8ff5c3'];
    const stonePalette = ['#1b3a29', '#244935', '#365b45'];

    return Array.from({ length: 3 }).map((_, index) => {
      const depth = index + 1;
      const trunks: ForestLayer['trunks'] = [];
      const canopy: ForestLayer['canopy'] = [];
      const stones: ForestLayer['stones'] = [];
      const treeCount = 16 - depth * 3;
      for (let i = 0; i < treeCount; i += 1) {
        const baseX = random.range(-40, ARENA.width + 40);
        const height = random.range(44, 84) * (1 - depth * 0.18);
        const width = random.range(6, 14) * (1 - depth * 0.22);
        trunks.push({ x: baseX, width, height, color: random.choice(trunkPalette) });
        const canopyCount = random.range(10, 20);
        for (let j = 0; j < canopyCount; j += 1) {
          canopy.push({
            x: baseX + random.range(-width * 2, width * 2),
            y: ARENA.groundY - height + random.range(-18, 12),
            radius: random.range(3, 8) * (1 - depth * 0.12),
            color: random.choice(leafPalette),
            swaySpeed: random.range(0.5, 1),
            swayMagnitude: random.range(0.4, 1.8),
            phase: random.range(0, Math.PI * 2),
          });
        }
      }
      const stoneCount = Math.round(treeCount * 0.4);
      for (let i = 0; i < stoneCount; i += 1) {
        stones.push({
          x: random.range(0, ARENA.width),
          width: random.range(10, 24) * (1 - depth * 0.12),
          height: random.range(8, 16) * (1 - depth * 0.15),
          color: random.choice(stonePalette),
        });
      }
      return { trunks, canopy, depth, stones };
    });
  }

  private renderForest(ctx: CanvasRenderingContext2D, time: number): void {
    if (!this.forest) {
      return;
    }
    const gradient = ctx.createLinearGradient(0, 0, 0, ARENA.height);
    gradient.addColorStop(0, '#021018');
    gradient.addColorStop(1, '#0a341f');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, ARENA.width, ARENA.height);

    this.forest.forEach((layer) => {
      const parallax = 1 - layer.depth * 0.25;
      ctx.save();
      ctx.translate((Math.sin(time * 0.08) * 4 * parallax) % ARENA.width, 0);
      ctx.globalAlpha = 1 - layer.depth * 0.18;

      layer.trunks.forEach((trunk) => {
        ctx.fillStyle = trunk.color;
        const baseY = ARENA.groundY;
        ctx.fillRect(trunk.x, baseY - trunk.height, trunk.width, trunk.height);
      });

      layer.stones.forEach((stone) => {
        ctx.fillStyle = stone.color;
        ctx.beginPath();
        ctx.ellipse(stone.x, ARENA.groundY - 4, stone.width, stone.height, 0, 0, Math.PI * 2);
        ctx.fill();
      });

      layer.canopy.forEach((leaf) => {
        const sway = Math.sin(time * leaf.swaySpeed + leaf.phase) * leaf.swayMagnitude;
        ctx.fillStyle = leaf.color;
        ctx.beginPath();
        ctx.arc(leaf.x + sway, leaf.y, leaf.radius, 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.restore();
    });

    ctx.fillStyle = 'rgba(14, 45, 30, 0.4)';
    ctx.fillRect(0, ARENA.height - 42, ARENA.width, 42);

    ctx.fillStyle = this.groundPalette.base;
    ctx.fillRect(0, ARENA.groundY, ARENA.width, ARENA.height - ARENA.groundY);
  }

  private generateDesert(random: SeededRandom): DesertScene {
    const dunes: DesertScene['dunes'] = [];
    const pyramidColors: Array<[string, string, string]> = [
      ['#bb8130', '#d89b3c', '#f2c261'],
      ['#a8702d', '#c38736', '#f1c26e'],
    ];
    const pyramids: DesertScene['pyramids'] = [];
    const dust: DesertScene['dust'] = [];

    for (let layer = 0; layer < 3; layer += 1) {
      dunes.push({
        baseY: ARENA.groundY - 12 + layer * 6,
        amplitude: 14 - layer * 3,
        phase: random.range(0, Math.PI * 2),
        color: ['#4f2d12', '#7f4a1b', '#a36523'][layer],
        speed: 0.06 + layer * 0.01,
      });
    }

    const pyramidCount = 2 + Math.round(random.range(0, 2));
    for (let i = 0; i < pyramidCount; i += 1) {
      pyramids.push({
        x: random.range(20, ARENA.width - 60),
        size: random.range(38, 58),
        colors: random.choice(pyramidColors),
      });
    }

    const dustCount = 28;
    for (let i = 0; i < dustCount; i += 1) {
      dust.push({
        x: random.range(0, ARENA.width),
        y: random.range(ARENA.groundY - 26, ARENA.groundY - 4),
        radius: random.range(1.5, 3.5),
        speed: random.range(0.3, 0.8),
        drift: random.range(0.5, 1.4),
      });
    }

    return { dunes, pyramids, dust };
  }

  private renderDesert(ctx: CanvasRenderingContext2D, time: number): void {
    if (!this.desert) {
      return;
    }

    const gradient = ctx.createLinearGradient(0, 0, 0, ARENA.height);
    gradient.addColorStop(0, '#2a0d21');
    gradient.addColorStop(0.5, '#5c1f24');
    gradient.addColorStop(1, '#b9632d');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, ARENA.width, ARENA.height);

    this.drawDesertSun(ctx);

    this.desert.dunes.forEach((dune) => {
      ctx.fillStyle = dune.color;
      ctx.beginPath();
      ctx.moveTo(0, ARENA.height);
      for (let x = 0; x <= ARENA.width; x += 6) {
        const wave = Math.sin((x + dune.phase * 40 + time * 30 * dune.speed) / 40);
        const y = dune.baseY - wave * dune.amplitude;
        ctx.lineTo(x, y);
      }
      ctx.lineTo(ARENA.width, ARENA.height);
      ctx.closePath();
      ctx.fill();
    });

    this.desert.pyramids.forEach((pyramid, index) => {
      const baseY = ARENA.groundY - 6 + index * 4;
      const size = pyramid.size;
      this.drawPyramid(ctx, pyramid.x, baseY, size, pyramid.colors);
    });

    ctx.save();
    ctx.globalAlpha = 0.6;
    this.desert.dust.forEach((particle, index) => {
      const driftX = (particle.x + time * 30 * particle.speed + index * 17) % ARENA.width;
      const sway = Math.sin(time * particle.speed + particle.drift) * 6;
      ctx.fillStyle = `rgba(255, 206, 128, ${0.25 + (particle.radius - 1.5) * 0.08})`;
      ctx.beginPath();
      ctx.arc(driftX, particle.y + sway, particle.radius, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();

    ctx.fillStyle = this.groundPalette.base;
    ctx.fillRect(0, ARENA.groundY, ARENA.width, ARENA.height - ARENA.groundY);
  }

  private drawDesertSun(ctx: CanvasRenderingContext2D): void {
    const sunX = ARENA.width - 60;
    const sunY = 40;
    const gradient = ctx.createRadialGradient(sunX, sunY, 6, sunX, sunY, 32);
    gradient.addColorStop(0, 'rgba(255, 236, 182, 0.9)');
    gradient.addColorStop(1, 'rgba(255, 148, 82, 0)');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(sunX, sunY, 32, 0, Math.PI * 2);
    ctx.fill();
  }

  private drawPyramid(
    ctx: CanvasRenderingContext2D,
    x: number,
    baseY: number,
    size: number,
    palette: [string, string, string],
  ): void {
    const height = size * 0.9;
    ctx.save();
    ctx.translate(x, baseY);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(size, 0);
    ctx.lineTo(size * 0.55, -height);
    ctx.closePath();
    ctx.fillStyle = palette[0];
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(size * 0.55, -height);
    ctx.lineTo(size * 0.1, -height * 0.9);
    ctx.closePath();
    ctx.fillStyle = palette[1];
    ctx.fill();

    ctx.strokeStyle = palette[2];
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(size * 0.55, -height);
    ctx.lineTo(size * 0.55, 0);
    ctx.stroke();
    ctx.restore();
  }

  private generateGlacier(random: SeededRandom): GlacierScene {
    const ridges: GlacierScene['ridges'] = [];
    const shards: GlacierScene['shards'] = [];
    const aurora: GlacierScene['aurora'] = [];

    const ridgeCount = 3;
    for (let i = 0; i < ridgeCount; i += 1) {
      ridges.push({
        x: random.range(-30, 30),
        width: ARENA.width + random.range(0, 40),
        height: random.range(60, 100) - i * 12,
        colors: [
          ['#123250', '#0c2035'][i % 2],
          ['#255b7c', '#1c4663'][i % 2],
        ] as [string, string],
        offset: random.range(-40, 40),
      });
    }

    const shardCount = 26;
    for (let i = 0; i < shardCount; i += 1) {
      shards.push({
        x: random.range(0, ARENA.width),
        y: ARENA.groundY - random.range(6, 24),
        height: random.range(12, 28),
        width: random.range(3, 7),
        sway: random.range(0.4, 1),
        phase: random.range(0, Math.PI * 2),
      });
    }

    const auroraBands = 3;
    for (let i = 0; i < auroraBands; i += 1) {
      aurora.push({
        amplitude: random.range(12, 22),
        frequency: random.range(0.015, 0.025),
        phase: random.range(0, Math.PI * 2),
        color: ['rgba(108, 255, 214, 0.25)', 'rgba(82, 190, 255, 0.2)', 'rgba(186, 255, 243, 0.18)'][i],
      });
    }

    return { ridges, shards, aurora };
  }

  private renderGlacier(ctx: CanvasRenderingContext2D, time: number): void {
    if (!this.glacier) {
      return;
    }

    const gradient = ctx.createLinearGradient(0, 0, 0, ARENA.height);
    gradient.addColorStop(0, '#030b1d');
    gradient.addColorStop(0.45, '#0b1d3a');
    gradient.addColorStop(1, '#143454');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, ARENA.width, ARENA.height);

    this.drawAurora(ctx, time);

    this.glacier.ridges.forEach((ridge, index) => {
      const parallax = 0.6 + index * 0.25;
      ctx.fillStyle = ridge.colors[0];
      ctx.beginPath();
      ctx.moveTo(-40, ARENA.groundY - 20);
      const steps = 14;
      for (let i = 0; i <= steps; i += 1) {
        const x = (ridge.width / steps) * i + ridge.offset;
        const variation = Math.sin((i / steps) * Math.PI * 2 + time * 0.1) * 12;
        const y = ARENA.groundY - ridge.height * parallax + variation;
        ctx.lineTo(x, y);
      }
      ctx.lineTo(ARENA.width + 40, ARENA.groundY - 20);
      ctx.closePath();
      ctx.fill();

      ctx.fillStyle = ridge.colors[1];
      ctx.globalAlpha = 0.4;
      ctx.beginPath();
      ctx.moveTo(-40, ARENA.groundY - 20);
      for (let i = 0; i <= steps; i += 1) {
        const x = (ridge.width / steps) * i + ridge.offset;
        const variation = Math.cos((i / steps) * Math.PI * 2 + time * 0.1) * 8;
        const y = ARENA.groundY - ridge.height * parallax * 0.86 + variation;
        ctx.lineTo(x, y);
      }
      ctx.lineTo(ARENA.width + 40, ARENA.groundY - 20);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;
    });

    ctx.fillStyle = 'rgba(180, 230, 255, 0.1)';
    this.glacier.shards.forEach((shard) => {
      const sway = Math.sin(time * shard.sway + shard.phase) * 3;
      ctx.save();
      ctx.translate(shard.x + sway, shard.y);
      ctx.fillRect(-shard.width / 2, -shard.height, shard.width, shard.height);
      ctx.restore();
    });

    ctx.fillStyle = this.groundPalette.base;
    ctx.fillRect(0, ARENA.groundY, ARENA.width, ARENA.height - ARENA.groundY);
  }

  private drawAurora(ctx: CanvasRenderingContext2D, time: number): void {
    if (!this.glacier) {
      return;
    }
    this.glacier.aurora.forEach((band, index) => {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.fillStyle = band.color;
      ctx.beginPath();
      const top = 30 + index * 12;
      ctx.moveTo(0, top + Math.sin(time * 0.15 + band.phase) * 6);
      for (let x = 0; x <= ARENA.width; x += 8) {
        const y = top + Math.sin(band.phase + time * 0.3 + x * band.frequency) * band.amplitude;
        ctx.lineTo(x, y);
      }
      ctx.lineTo(ARENA.width, top + 60);
      ctx.lineTo(0, top + 60);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    });
  }
}
